/*
 * What people actually do on a page, in six events.
 *
 * WHY. Until now the loop could say a change shipped and never what it did.
 * The only signal on a café page was pageviews, and those are driven by search
 * rather than by whether the page is any good, so grading a photo gallery on
 * them measures the wrong thing entirely. Four of the five things shipped
 * before September could be verified as present and never scored.
 *
 * WHAT THIS IS NOT. It is not a second analytics install. Everything goes
 * through window.offshoreTrack, which already fans out to dataLayer, gtag and
 * Shopify's analytics bus, and which already carries quiz_complete and
 * newsletter_signup. Loading our own GA4 tag alongside Shopify's Google
 * channel would risk a second client id and double-counted visitors, which
 * would corrupt the exact numbers the weekly report is built from.
 *
 * SIX EVENTS, FIXED. A taxonomy designed once is worth more than thirty added
 * one at a time, because a name chosen in a hurry is a name every future
 * comparison is stuck with. Adding one later is fine; renaming one is not.
 *
 *   cta_click         a call to action was clicked        cta, page_type, href
 *   gallery_interact  somebody moved the photo gallery    page_type, method
 *   scroll_75         three quarters of the page reached  page_type
 *   form_start        somebody began filling a form       form, page_type
 *   subscribe_toggle  the subscribe option was chosen     choice, page_type
 *   outbound_click    a link off the site was taken       host, link_domain, page_type
 *
 * EVERYTHING IS DELEGATED from document, so no template needs editing and
 * markup rendered later is covered automatically. Every handler is wrapped:
 * an analytics call must never be the reason something on the page stops
 * working, which is the same rule theme-analytics.js already follows.
 *
 * Each event fires AT MOST ONCE PER PAGE VIEW. Without that, scroll_75 fires
 * on every wheel tick and one restless visitor outweighs a hundred real ones.
 */
(function engagement() {
  var track = function (name, data) {
    try {
      if (typeof window.offshoreTrack === 'function') window.offshoreTrack(name, data || {});
    } catch (error) {
      /* deliberately silent */
    }
  };

  /* The template, from the class Shopify already puts on <body>: "template-page",
     "template-product", plus "template-suffix-location" where a suffix exists.
     The suffix is the more useful one when it is there, because a café page and
     a policy page are both "page" and nothing like each other. */
  function pageType() {
    try {
      var cls = document.body.className || '';
      var suffix = /template-suffix-([a-z0-9-]+)/.exec(cls);
      if (suffix) return suffix[1];
      var base = /template-([a-z0-9-]+)/.exec(cls);
      return base ? base[1] : 'unknown';
    } catch (error) {
      return 'unknown';
    }
  }

  var PAGE = pageType();
  var fired = {};
  /* form_start is once per FORM, not once per page: a page can carry a
     newsletter box and a contact form, and collapsing them would hide which
     one people abandon. */
  var formsStarted = {};
  function once(name, data) {
    if (fired[name]) return;
    fired[name] = true;
    track(name, data);
  }

  /* A short, stable label for a button, so the report can say "order ahead"
     rather than quote whatever the button happened to say that month. */
  function ctaName(el) {
    var explicit = el.getAttribute('data-cta');
    if (explicit) return explicit;
    var text = (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    return text.slice(0, 40) || 'unlabelled';
  }

  /* ------------------------------- clicks ------------------------------- */

  document.addEventListener(
    'click',
    function (event) {
      try {
        var link = event.target.closest && event.target.closest('a, button');
        if (!link) return;

        /* Off-site: order-ahead, booking, maps, socials. Worth separating,
           because a click that leaves for Square is a success on a café page
           and a leak on a product page. */
        var href = link.getAttribute('href') || '';
        if (/^https?:\/\//i.test(href)) {
          var host = '';
          var domain = '';
          try { var destination = new URL(href); host = destination.host; domain = destination.hostname; } catch (error) { host = ''; }
          if (host && host !== window.location.host) {
            /* GA4's standard linkDomain dimension reads link_domain. Keep
               host and the event name for existing consumers; never send
               the destination's query string or credentials. */
            track('outbound_click', { host: host, link_domain: domain, page_type: PAGE, cta: ctaName(link) });
            return;
          }
        }

        /* Gallery arrows count as gallery use, not as a call to action. */
        if (link.closest('.main-location__gallery-nav') ||
            (link.className && String(link.className).indexOf('main-location__gallery-nav') > -1)) {
          once('gallery_interact', { page_type: PAGE, method: 'arrow' });
          return;
        }

        /* An in-page jump is not a call to action. The skip-to-content link
           carries the button class and fired on the very first test, which
           would have put an accessibility control at the top of the CTA
           report every week. */
        if (href.charAt(0) === '#') return;

        /* Anything styled as a button, plus anything explicitly tagged. One
           event with a parameter beats six near-identical event names. */
        if (link.hasAttribute('data-cta') ||
            (link.className && String(link.className).split(/\s+/).indexOf('button') > -1)) {
          track('cta_click', { cta: ctaName(link), page_type: PAGE, href: href.slice(0, 120) });
        }
      } catch (error) {
        /* deliberately silent */
      }
    },
    true
  );

  /* ------------------------------- gallery ------------------------------ */

  /* Scrolling the track is the mobile gesture and the desktop one; the arrows
     are handled above. Passive so it can never delay a swipe. */
  try {
    var galleryTrack = document.querySelector('.main-location__gallery-track');
    if (galleryTrack) {
      galleryTrack.addEventListener(
        'scroll',
        function () { once('gallery_interact', { page_type: PAGE, method: 'swipe' }); },
        { passive: true, once: true }
      );
    }
  } catch (error) {
    /* deliberately silent */
  }

  /* -------------------------------- depth ------------------------------- */

  try {
    var onScroll = function () {
      try {
        var doc = document.documentElement;
        var scrollable = doc.scrollHeight - window.innerHeight;
        /* A page shorter than the window is fully read on arrival; counting it
           as a 75% scroll would flatter every short page. */
        if (scrollable < 400) return;
        if ((window.scrollY || doc.scrollTop) / scrollable >= 0.75) {
          once('scroll_75', { page_type: PAGE });
          window.removeEventListener('scroll', onScroll);
        }
      } catch (error) {
        /* deliberately silent */
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  } catch (error) {
    /* deliberately silent */
  }

  /* -------------------------------- forms ------------------------------- */

  /* The first real keystroke or choice, not focus: focus fires when somebody
     tabs through on the way past, and counting that as an attempt makes every
     abandonment rate look worse than it is. */
  document.addEventListener(
    'input',
    function (event) {
      try {
        var form = event.target.closest && event.target.closest('form');
        if (!form) return;
        var name = form.getAttribute('data-form-name') ||
          form.getAttribute('id') ||
          form.getAttribute('action') ||
          'form';
        if (formsStarted[name]) return;
        formsStarted[name] = true;
        track('form_start', { form: String(name).slice(0, 60), page_type: PAGE });
      } catch (error) {
        /* deliberately silent */
      }
    },
    true
  );

  /* ----------------------------- subscription --------------------------- */

  /* One-time versus subscribe, on the product page. The default is one-time on
     purpose, so what this measures is people actively choosing the recurring
     option, which is the thing the business wants to know. */
  document.addEventListener(
    'change',
    function (event) {
      try {
        var input = event.target;
        if (!input || input.type !== 'radio') return;
        /* data-plan-radio is on both options in selling-plan-picker.liquid.
           The one-time radio carries value="" and the subscribe one carries
           the selling plan id, so a non-empty value is the recurring choice. */
        if (!input.hasAttribute('data-plan-radio')) return;
        track('subscribe_toggle', {
          choice: input.value ? 'subscribe' : 'one_time',
          page_type: PAGE,
        });
      } catch (error) {
        /* deliberately silent */
      }
    },
    true
  );
})();
